import {t, useLanguage} from './i18n';
// src/OwnerBusinessesPage.js
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar,
  FileText,
  BarChart2,
  Edit3,
  ExternalLink,
  Store,
  Plus,
  ArrowLeft,
  Search,
  Eye,
  Building2,
  SlidersHorizontal,
  Tag,
  MapPin,
  Play,
} from "lucide-react";
import './OwnerBusinessesPage.css';
import { auth } from "./firebase";
import { getManualAuth, isManualAuthLikelyValid } from "./utils/manualAuth";
import { getBusinessRouteParam, businessPublicPath, businessAdminPath } from "./utils/businessSlug";

/**
 * OwnerBusinessesPage — Command Center Revamp
 * - Dashboard compacto premium para administrar empresas.
 * - Mantiene rutas actuales.
 * - Acción principal: abrir calendario.
 * - Acciones secundarias: pendientes, editar y más.
 */

const BRAND = "#00675f";
const BRAND_2 = "#14b8a6";
const TEXT = "#101828";
const MUTED = "#667085";
const BORDER = "#e7ded2";

function getStatus(business) {
  const rawStatus = String(business?.approvalStatus || business?.status || "").toLowerCase();
  const visible =
    typeof business?.visible !== "undefined"
      ? !!business.visible
      : typeof business?.hidden !== "undefined"
        ? !business.hidden
        : true;

  if (rawStatus === "pending" || rawStatus === "pending_approval") {
    return { label: "En revisión", tone: "pending" };
  }

  if(rawStatus === 'changes_requested') return {label:'Necesita ajustes',tone:'draft'};
  if(rawStatus === 'rejected') return {label:'Solicitud rechazada',tone:'draft'};
  if (["draft", "incomplete"].includes(rawStatus)) return { label: "En preparación", tone: "draft" };

  if (!visible || rawStatus === "paused") {
    return { label: "Perfil oculto", tone: "paused" };
  }

  return { label: "Activa", tone: "active" };
}

function getBusinessImage(business) {
  return (
    business?.logoUrl ||
    business?.mainPhotoUrl ||
    business?.photoUrl ||
    business?.imageUrl ||
    business?.coverImage ||
    ""
  );
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getBusinessType(business) {
  return business?.type || business?.category || business?.businessType || "Negocio";
}

function getBusinessAddress(business) {
  const raw = business?.address || business?.location || "";
  if (typeof raw !== 'string') return '';
  const seen = new Set();
  return raw.split(',').map(part => part.trim()).filter(part => {const key = normalizeText(part); if (!key || seen.has(key)) return false; seen.add(key); return true;}).join(', ');
}

function SkeletonCard() {
  return (
    <article className="ob-card ob-skeleton-card">
      <div className="ob-card-head">
        <div className="ob-skeleton-logo" />
        <div className="ob-skeleton-lines">
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="ob-skeleton-button" />
      <div className="ob-skeleton-actions" />
    </article>
  );
}

function EmptyState({ onGoBack, onCreateBusiness }) {
  return (
    <div className="ob-empty">
      <div className="ob-empty-icon">
        <Store size={28} />
      </div>
      <h2>{t("No encontré empresas")}</h2>
      <p>
        Tus empresas aparecerán aquí para administrar su calendario, reservas y
        configuración, aunque su perfil esté oculto al público o en preparación.
      </p>
      <div className="ob-empty-actions">
        <button onClick={onCreateBusiness} className="ob-primary-mini-btn">
          <Plus size={16} />{t("Crear empresa")}</button>
        <button onClick={onGoBack} className="ob-secondary-btn">
          <ArrowLeft size={16} />{" " + t("Volver")}</button>
      </div>
    </div>
  );
}

function BusinessAvatar({ business }) {
  const img = getBusinessImage(business);
  const initial = String(business?.name || "N").slice(0, 1).toUpperCase();

  return (
    <div className="ob-avatar ob-logo-frame">
      {img ? (
        <img
          src={img}
          alt={business?.name || t("Negocio")}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  return (
    <div className={`ob-stat-card ${tone}`}>
      <span className="ob-stat-accent" />
      <strong>{value}</strong>
      <small>{label}</small>
    </div>
  );
}

function StatusChip({ status }) {
  return <span className={`ob-status ${status.tone}`}>{t(status.label)}</span>;
}

function MetaItem({ icon, children }) {
  if (!children) return null;
  return (
    <span className="ob-meta-item">
      {icon}
      <span>{children}</span>
    </span>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div className="ob-section-header">
      <h2>{title}</h2>
      <span>{count}</span>
    </div>
  );
}

function BusinessCard({business, onCalendar, onEdit, onOpen}) {
  const status = getStatus(business);
  const [showStatus, setShowStatus] = useState(false);
  const active = status.tone === 'active' || status.tone === 'paused';
  const pending = status.tone === 'pending';
  return <article className="company-row">
    <button className="company-identity" type="button" onClick={event => onEdit(event, business)} aria-label={"Editar " + (business.name || "negocio")}>
      <BusinessAvatar business={business} />
      <span className="company-copy"><strong>{business.name || t("Negocio")}</strong><span>{getBusinessAddress(business) || getBusinessType(business)}</span></span>
    </button>
    <StatusChip status={status} />
    <div className="company-actions">
      <button className={active ? 'company-primary' : 'company-secondary'} type="button" onClick={event => active ? onCalendar(event,business) : pending ? setShowStatus(value => !value) : onEdit(event,business)} aria-expanded={pending ? showStatus : undefined}>
        {active ? <Calendar size={16}/> : <Edit3 size={16}/>}{active ? 'Abrir agenda' : pending ? 'Ver estado' : status.tone === 'draft' ? 'Continuar configuración' : t("Editar negocio")}
      </button>
      {!active && <button type="button" className="company-secondary" onClick={event => onCalendar(event,business)}>{t("Agenda privada")}</button>}
      {(active || pending) && <button type="button" className="company-secondary" onClick={event => onEdit(event,business)}>{t("Editar")}</button>}
      {status.tone === 'active' && <details className="company-more"><summary aria-label={"Más acciones para " + (business.name || "negocio")}>⋯</summary><div><button type="button" onClick={event => onOpen(event,business)}>{t("Ver perfil público") + " "}<ExternalLink size={14}/></button></div></details>}
    </div>
    {showStatus && <div className="company-status-note" role="status"><strong>{t("En revisión")}</strong><p>{t("Tu negocio está esperando aprobación. Puedes revisar y editar su información mientras se completa la revisión.")}</p></div>}
  </article>;
}

export default function OwnerBusinessesPage({
  businesses = [],
  loading = false,
  onGoBack = () => {},
  onViewCalendar,
  onGoToBooking,
  onEditBusiness,
  onCreateBusiness,
}) {
  useLanguage();
  const navigate = useNavigate();

  // Align web and native routes with the visible header before paint.
  React.useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;


    const media = window.matchMedia("(max-width: 900px)");
    if (!media.matches) return undefined;

    let rafId = 0;
    let resizeObserver = null;

    const alignRoute = () => {
      const page = document.querySelector(".ob-page");
      const header = document.querySelector("#app-main-header, .loop-ref-header, .loop-mobile-header-v2");
      if (!page || !header || !page.isConnected || !header.isConnected) return;

      const headerRect = header.getBoundingClientRect();
      const pageRect = page.getBoundingClientRect();
      if (!Number.isFinite(headerRect.bottom) || !Number.isFinite(pageRect.top)) return;

      const targetTop = Math.ceil(headerRect.bottom);
      const delta = targetTop - (pageRect.top + window.scrollY);
      const currentMargin = Number.parseFloat(window.getComputedStyle(page).marginTop || "0") || 0;

      if (Math.abs(delta) > 0.5) {
        const nextMargin = Math.round((currentMargin + delta) * 10) / 10;
        page.style.setProperty("margin-top", `${nextMargin}px`, "important");
      }

      page.dataset.loopHeaderClearanceOwner = "V58-owner-businesses";
      page.dataset.loopHeaderBottom = String(targetTop);
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(alignRoute);
    };

    // useLayoutEffect calls once synchronously before paint; RAF is only a resize safety net.
    alignRoute();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    try {
      if (typeof ResizeObserver !== "undefined") {
        const header = document.querySelector("#app-main-header, .loop-ref-header, .loop-mobile-header-v2");
        if (header) {
          resizeObserver = new ResizeObserver(schedule);
          resizeObserver.observe(header);
        }
      }
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      try { resizeObserver?.disconnect?.(); } catch (_) {}
      const page = document.querySelector(".ob-page");
      if (page?.dataset?.loopHeaderClearanceOwner === "V58-owner-businesses") {
        try { page.style.removeProperty("margin-top"); } catch (_) {}
      }
    };
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryText = searchParams.get('q') || '';
  const statusFilter = searchParams.get('estado') || 'all';
  const sortOrder = searchParams.get('orden') || 'name';
  const setFilter = (key,value) => setSearchParams(prev => {const next = new URLSearchParams(prev); if(value) next.set(key,value); else next.delete(key); return next;}, {replace:true});
  const setQueryText = value => setFilter('q',value);
  const setStatusFilter = value => setFilter('estado',value);

  const stats = useMemo(() => {
    const list = Array.isArray(businesses) ? businesses : [];
    const total = list.length;
    const active = list.filter((b) => getStatus(b).tone === "active").length;
    const paused = list.filter((b) => getStatus(b).tone === "paused").length;
    const pending = list.filter((b) => getStatus(b).tone === "pending").length;
    return { total, active, paused, pending, draft: list.filter(b => getStatus(b).tone === "draft").length };
  }, [businesses]);

  const filteredBusinesses = useMemo(() => {
    const q = normalizeText(queryText);
    return (Array.isArray(businesses) ? businesses : []).filter((b) => {
      const status = getStatus(b).tone;
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!q) return true;
      const haystack = normalizeText([b?.name, getBusinessType(b), getBusinessAddress(b)].filter(Boolean).join(" "));
      return haystack.includes(q);
    });
  }, [businesses, queryText, statusFilter]);

  const sortedBusinesses = useMemo(() => [...filteredBusinesses].sort((a,b) => {
    if (sortOrder === 'recent') {
      const time = value => value?.toMillis?.() || (value?.seconds ? value.seconds * 1000 : Date.parse(value || '') || 0);
      const difference = time(b.updatedAt || b.createdAt) - time(a.updatedAt || a.createdAt);
      if (difference) return difference;
    }
    return String(a.name || '').localeCompare(String(b.name || ''), 'es');
  }), [filteredBusinesses, sortOrder]);

  const groupedBusinesses = useMemo(() => {
    const groups = { active: [], paused: [], pending: [] };
    filteredBusinesses.forEach((b) => {
      const tone = getStatus(b).tone;
      if (groups[tone]) groups[tone].push(b);
    });
    return groups;
  }, [filteredBusinesses]);

  const defaultGoToCalendar = (b) => {
    const path = businessAdminPath(b, "calendar");
    try {
      navigate(path);
    } catch (e) {
      try {
        window.location.assign(`${window.location.origin}${path}`);
      } catch (_) {}
    }
  };

  const defaultGoToPendings = (b) => {
    const path = `/admin/pending-reservations?businessId=${encodeURIComponent(b.id)}`;
    const originUrl = `${window.location.origin}${path}`;

    try {
      navigate(path);
    } catch (err) {
      console.warn("[OwnerBusinesses] navigate failed:", err);
    }

    setTimeout(() => {
      const current = window.location.pathname + window.location.search;
      if (current === path) return;

      const webUser = auth && auth.currentUser ? auth.currentUser : null;

      let hasEffectiveAuth = Boolean(webUser);
      if (!hasEffectiveAuth) {
        try {
          const ma = getManualAuth();
          hasEffectiveAuth = Boolean(ma && isManualAuthLikelyValid(ma) && ma.idToken);
        } catch (_) {}
      }

      if (hasEffectiveAuth) {
        try {
          window.location.assign(originUrl);
          return;
        } catch (e) {
          try {
            window.location.href = originUrl;
            return;
          } catch (_) {
            console.error("[OwnerBusinesses] full reload fallback failed", e);
          }
        }
      }

      try {
        sessionStorage.setItem("intent_admin_pending_after_login", JSON.stringify({ businessId: b.id }));
      } catch (_) {}

      try {
        navigate("/login");
      } catch (_) {
        window.location.assign(`${window.location.origin}/login`);
      }
    }, 700);
  };

  const defaultGoToBooking = (b) => {
    const path = `/booking?businessId=${encodeURIComponent(b.id)}&businessName=${encodeURIComponent(b.name || "")}`;
    try {
      navigate(path);
    } catch (_) {
      window.location.assign(`${window.location.origin}${path}`);
    }
  };

  const defaultEditBusiness = (b) => {
    try {
      navigate(`/business/${encodeURIComponent(getBusinessRouteParam(b) || b.id)}/edit`);
    } catch (_) {
      window.location.assign(`${window.location.origin}/business/${encodeURIComponent(getBusinessRouteParam(b) || b.id)}/edit`);
    }
  };

  const defaultCreateBusiness = () => {
    try {
      navigate("/new-business");
    } catch (_) {
      window.location.assign(`${window.location.origin}/new-business`);
    }
  };

  const handleCreateBusiness = () => {
    if (typeof onCreateBusiness === "function") return onCreateBusiness();
    return defaultCreateBusiness();
  };

  const handleCalendar = (e, b) => {
    e && e.stopPropagation();
    if (typeof onViewCalendar === "function") return onViewCalendar(b);
    return defaultGoToCalendar(b);
  };

  const handlePendings = (e, b) => {
    e && e.stopPropagation();
    return defaultGoToPendings(b);
  };

  const handleBooking = (e, b) => {
    e && e.stopPropagation();
    if (typeof onGoToBooking === "function") return onGoToBooking(b);
    return defaultGoToBooking(b);
  };

  const handleEdit = (e, b) => {
    e && e.stopPropagation();
    if (typeof onEditBusiness === "function") return onEditBusiness(b);
    return defaultEditBusiness(b);
  };

  const handleStats = (e, b) => {
    e && e.stopPropagation();
    if (!b || !b.id) return;

    const path = businessAdminPath(b, "analytics");

    try {
      navigate(path);
    } catch (_) {
      try {
        window.location.assign(`${window.location.origin}${path}`);
      } catch (_) {}
    }
  };

  const handleOpenInNew = (e, b) => {
    e && e.stopPropagation();
    window.open(businessPublicPath(b), "_blank", "noopener,noreferrer");
  };

  const handleCardClick = (e, b) => {
    try {
      const tgt = e && e.target ? e.target : null;
      if (tgt && tgt.closest) {
        if (tgt.closest("button") || tgt.closest("a") || tgt.closest("input")) return;
      }
    } catch (_) {}
    handleEdit(e, b);
  };


  const renderSection = (title, list, variant = "default") => {
    if (!list.length) return null;
    return (
      <section className={`ob-business-section ${variant}`}>
        <SectionHeader title={title} count={list.length} />
        <div className={`ob-business-list ${variant}`}>
          {list.map((b) => (
            <BusinessCard
              key={b.id}
              business={b}
              variant={variant}
              onCalendar={handleCalendar}
              onEdit={handleEdit}
              onOpen={handleOpenInNew}
              onPendings={handlePendings}
              onStats={handleStats}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      </section>
    );
  };

  const renderFilterButtons = (compact = false) => (
    <div className={`ob-filter-group ${compact ? "is-compact" : ""}`} aria-label={t("Filtrar empresas")}>
      <span className="ob-filter-icon"><SlidersHorizontal size={15} /></span>
      {[
        ["all", "Todas"],
        ["active", "Activas"],
        ["paused", "Pausadas"],
        ["pending", "Pendientes"],
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          className={statusFilter === value ? "is-active" : ""}
          onClick={() => setStatusFilter(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const renderSearch = (compact = false) => (
    <label className={`ob-search ${compact ? "is-compact" : ""}`} aria-label={t("Buscar empresa")}>
      <Search size={17} />
      <input
        value={queryText}
        onChange={(e) => setQueryText(e.target.value)}
        placeholder={t("Buscar empresa...")}
      />
    </label>
  );

  const renderSummary = (compact = false) => (
    <section className={`ob-stats-row ${compact ? "is-compact" : ""}`} aria-label={t("Resumen de empresas")}>
      <StatCard label="Total" value={stats.total} />
      <StatCard label={t("Activas")} value={stats.active} tone="active" />
      <StatCard label={t("Pausadas")} value={stats.paused} tone="paused" />
      <StatCard label="Pendientes" value={stats.pending} tone="pending" />
    </section>
  );

  const renderBusinesses = () => (
    filteredBusinesses.length === 0 ? (
      <div className="ob-filter-empty">
        <Building2 size={24} />
        <strong>{t("No hay empresas con ese filtro")}</strong>
        <span>{t("Prueba cambiar la búsqueda o seleccionar “Todas”.")}</span>
      </div>
    ) : (
      <div className="ob-sections-stack">
        {renderSection("Activas", groupedBusinesses.active, "active")}
        {renderSection("Pausadas", groupedBusinesses.paused, "paused")}
        {renderSection("Pendientes", groupedBusinesses.pending, "pending")}
      </div>
    )
  );

  const renderPageHeader = () => (<header className="company-header"><div><button type="button" className="company-back" onClick={onGoBack}><ArrowLeft size={15}/>{" " + t("Volver")}</button><h1>{t("Mis empresas")}</h1><p>{t("Abre una agenda o gestiona tu negocio.")}</p></div><button type="button" className="company-primary" onClick={handleCreateBusiness}><Plus size={17}/>{t("Crear negocio")}</button></header>);

  if (loading) {
    return <div className="ob-page company-page" aria-busy="true"><style>{styles}</style><div className="company-shell">
      {renderPageHeader()}
      <div className="company-loading" role="status"><span>{t("Cargando tus negocios…")}</span>{[0,1,2].map(key => <div className="company-loading-row" key={key} aria-hidden="true" />)}</div>
    </div></div>;
  }

  if (!businesses || businesses.length === 0) {
    return <div className="ob-page company-page"><style>{styles}</style><div className="company-shell">
      {renderPageHeader()}
      <div className="company-empty"><Building2 size={24}/><h2>{t("Aún no tienes negocios")}</h2><p>{t("Crea tu primer negocio para comenzar a recibir reservas.")}</p></div>
    </div></div>;
  }

  return <div className="ob-page company-page"><style>{styles}</style><div className="company-shell">
    {renderPageHeader()}
    <section className="company-toolbar" aria-label={t("Buscar y filtrar negocios")}><label className="company-search"><Search size={17}/><input type="search" aria-label={t("Buscar negocio")} placeholder={t("Buscar negocio…")} value={queryText} onChange={event => setQueryText(event.target.value)}/></label><label className="company-sort">{t("Ordenar por")}<select aria-label={t("Ordenar negocios")} value={sortOrder} onChange={event => setFilter('orden',event.target.value)}><option value="name">{t("Nombre")}</option><option value="recent">{t("Más recientes")}</option></select></label><div className="company-filters">{[['all','Todos',stats.total],['active','Activos',stats.active],['paused','Pausados',stats.paused],['pending','En revisión',stats.pending],...(stats.draft ? [['draft','Sin terminar',stats.draft]] : [])].map(([value,label,count]) => <button type="button" key={value} aria-pressed={statusFilter===value} onClick={() => setStatusFilter(value)}>{t(label)}<span>{count}</span></button>)}</div></section>
    <main className="company-list" aria-label={t("Tus negocios")}>{sortedBusinesses.length ? sortedBusinesses.map(business => <BusinessCard key={business.id} business={business} onEdit={handleEdit} onCalendar={handleCalendar} onOpen={handleOpenInNew}/>) : <div className="company-empty"><Building2 size={24}/><h2>{t("No hay coincidencias")}</h2><p>{t("Prueba otro nombre o cambia el filtro.")}</p><button className="company-secondary" onClick={() => {setSearchParams({}, {replace:true});}}>{t("Limpiar filtros")}</button></div>}</main>
  </div></div>;
}

const styles = `
  .ob-page {
    --ob-bg: #fbf6ee;
    --ob-surface: rgba(255,255,255,0.92);
    --ob-surface-strong: #ffffff;
    --ob-border: ${BORDER};
    --ob-text: ${TEXT};
    --ob-muted: ${MUTED};
    --ob-brand: ${BRAND};
    --ob-brand-dark: #004f49;
    --ob-brand-soft: #e6f5f2;
    --ob-shadow-soft: 0 12px 30px rgba(16,24,40,0.045);
    --ob-shadow-card: 0 16px 42px rgba(16,24,40,0.060);
    width: 100vw;
    min-height: 100vh;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding: 18px max(18px, calc((100vw - 1180px) / 2)) calc(112px + env(safe-area-inset-bottom, 0px));
    background:
      radial-gradient(circle at 18% -10%, rgba(0,103,95,0.045), transparent 360px),
      linear-gradient(180deg, #fffaf3 0%, #f8f1e8 100%) !important;
    color: var(--ob-text);
    box-sizing: border-box;
  }

  .ob-page * {
    box-sizing: border-box;
  }

  .ob-shell {
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    gap: 18px;
  }

  .ob-header-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 22px;
    padding: 0 2px;
  }

  .ob-title-block {
    min-width: 0;
  }

  .ob-pretitle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    min-width: 0;
  }

  .ob-back {
    width: fit-content;
    min-height: 32px;
    border: 1px solid rgba(231,222,210,0.95);
    background: rgba(255,255,255,0.78);
    color: #344054;
    border-radius: 999px;
    padding: 0 12px;
    font-size: 12.5px;
    font-weight: 850;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    box-shadow: 0 8px 18px rgba(16,24,40,0.035);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    flex: 0 0 auto;
  }

  .ob-back:hover {
    color: var(--ob-brand);
    border-color: rgba(0,103,95,0.22);
  }

  .ob-kicker {
    display: inline-flex;
    align-items: center;
    color: var(--ob-brand);
    font-size: 12px;
    font-weight: 950;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin: 0;
    min-height: 32px;
  }

  .ob-header-row h1 {
    margin: 0;
    color: #090f1f;
    font-size: clamp(42px, 4.2vw, 62px);
    line-height: 0.94;
    font-weight: 950;
    letter-spacing: -0.078em;
  }

  .ob-header-row p {
    margin: 13px 0 0;
    max-width: 650px;
    color: var(--ob-muted);
    font-size: 15px;
    line-height: 1.45;
    font-weight: 700;
  }

  .ob-create-btn,
  .ob-primary-mini-btn {
    min-height: 46px;
    border: 0;
    border-radius: 15px;
    padding: 0 19px;
    background: linear-gradient(135deg, var(--ob-brand-dark), var(--ob-brand));
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    font-size: 14px;
    font-weight: 900;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 16px 34px rgba(0,79,73,0.18);
    transition: transform 150ms ease, box-shadow 150ms ease;
  }

  .ob-create-btn:hover,
  .ob-primary-mini-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 20px 44px rgba(0,79,73,0.24);
  }

  .ob-stats-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px;
  }

  .ob-stat-card {
    position: relative;
    min-height: 86px;
    border-radius: 18px;
    border: 1px solid rgba(231,222,210,0.96);
    background: rgba(255,255,255,0.82);
    box-shadow: 0 12px 28px rgba(16,24,40,0.045);
    padding: 19px 20px;
    display: grid;
    align-content: center;
    overflow: hidden;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
  }

  .ob-stat-accent {
    position: absolute;
    top: 0;
    left: 0;
    width: 76px;
    height: 3px;
    border-radius: 999px;
    background: #98a2b3;
  }

  .ob-stat-card.active .ob-stat-accent { background: var(--ob-brand); }
  .ob-stat-card.paused .ob-stat-accent { background: #f59e0b; }
  .ob-stat-card.pending .ob-stat-accent { background: #94a3b8; }

  .ob-stat-card strong {
    color: var(--ob-text);
    font-size: 30px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -0.06em;
  }

  .ob-stat-card small {
    margin-top: 8px;
    color: #667085;
    font-size: 12px;
    font-weight: 850;
    letter-spacing: 0.01em;
  }

  .ob-control-bar {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) auto;
    gap: 18px;
    align-items: center;
    padding: 2px 0 0;
  }

  .ob-search {
    min-width: 0;
    height: 48px;
    border-radius: 999px;
    border: 1px solid rgba(231,222,210,0.96);
    background: rgba(255,255,255,0.84);
    color: #667085;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 0 17px;
    box-shadow: 0 10px 24px rgba(16,24,40,0.035);
  }

  .ob-search input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--ob-text);
    font-size: 14px;
    font-weight: 750;
  }

  .ob-search input::placeholder {
    color: #98a2b3;
  }

  .ob-filter-group {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .ob-filter-group::-webkit-scrollbar { display: none; }

  .ob-filter-icon {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    background: rgba(0,103,95,0.075);
    color: var(--ob-brand);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .ob-filter-group button {
    flex: 0 0 auto;
    height: 38px;
    border-radius: 999px;
    border: 1px solid rgba(231,222,210,0.96);
    background: rgba(255,255,255,0.74);
    color: #475467;
    padding: 0 16px;
    font-size: 12.5px;
    font-weight: 850;
    cursor: pointer;
    box-shadow: 0 6px 14px rgba(16,24,40,0.022);
  }

  .ob-filter-group button.is-active {
    background: #101828;
    color: #ffffff;
    border-color: #101828;
  }

  .ob-sections-stack {
    position: relative;
    z-index: 1;
    display: grid;
    overflow: visible;
    gap: 22px;
    margin-top: -2px;
  }

  .ob-business-section {
    position: relative;
    display: grid;
    overflow: visible;
    gap: 14px;
  }

  .ob-section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .ob-section-header h2 {
    margin: 0;
    color: var(--ob-text);
    font-size: 18px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: -0.035em;
    flex: 0 0 auto;
  }

  .ob-section-header span {
    min-width: 28px;
    height: 24px;
    border-radius: 999px;
    background: rgba(255,255,255,0.74);
    border: 1px solid rgba(231,222,210,0.92);
    color: #667085;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 900;
    padding: 0 8px;
    flex: 0 0 auto;
  }

  .ob-section-header::after {
    content: "";
    height: 1px;
    background: rgba(209,213,219,0.78);
    flex: 1 1 auto;
    min-width: 24px;
  }

  .ob-business-list {
    position: relative;
    display: grid;
    overflow: visible;
    gap: 14px;
  }

  .ob-business-list.paused,
  .ob-business-list.pending {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .ob-business-card {
    position: relative;
    width: 100%;
    min-height: 118px;
    border-radius: 18px;
    border: 1px solid rgba(231,222,210,0.96);
    background: rgba(255,255,255,0.88);
    box-shadow: var(--ob-shadow-card);
    padding: 18px;
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr) auto;
    align-items: center;
    gap: 18px;
    cursor: pointer;
    transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background 160ms ease;
    overflow: visible;
  }

  .ob-business-card.active {
    min-height: 126px;
    background: rgba(255,255,255,0.92);
  }

  .ob-business-card.paused,
  .ob-business-card.pending {
    min-height: 134px;
    grid-template-columns: 72px minmax(0, 1fr);
    align-items: start;
    gap: 16px;
    padding: 18px;
    box-shadow: 0 12px 32px rgba(16,24,40,0.050);
  }


  .ob-business-card.is-menu-open {
    z-index: 999;
    border-color: rgba(0,103,95,0.26);
  }

  .ob-business-card:hover {
    transform: translateY(-2px);
    border-color: rgba(0,103,95,0.24);
    box-shadow: 0 22px 56px rgba(16,24,40,0.085);
    background: rgba(255,255,255,0.97);
  }

  .ob-avatar,
  .ob-logo-frame {
    width: 80px;
    height: 80px;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid rgba(231,222,210,0.96);
    display: grid;
    place-items: center;
    box-shadow: 0 10px 22px rgba(16,24,40,0.055);
    padding: 7px;
    flex: 0 0 auto;
  }

  .ob-business-card.paused .ob-avatar,
  .ob-business-card.pending .ob-avatar {
    width: 72px;
    height: 72px;
  }

  .ob-avatar img,
  .ob-logo-frame img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    border-radius: 8px;
  }

  .ob-avatar span,
  .ob-logo-frame span {
    color: #98a2b3;
    font-size: 24px;
    font-weight: 950;
  }

  .ob-business-info {
    min-width: 0;
  }

  .ob-title-line {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-bottom: 9px;
  }

  .ob-title-line h3 {
    margin: 0;
    color: var(--ob-text);
    font-size: 20px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ob-business-card.active .ob-title-line h3 { font-size: 21px; }

  .ob-status {
    display: inline-flex;
    width: fit-content;
    border-radius: 8px;
    padding: 5px 8px;
    font-size: 10.5px;
    font-weight: 950;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .ob-status.active { color: #047857; background: #dff8ef; }
  .ob-status.paused { color: #667085; background: #f1f5f9; }
  .ob-status.pending { color: #a16207; background: #fef3c7; }

  .ob-meta-row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex-wrap: wrap;
  }

  .ob-meta-item {
    min-width: 0;
    max-width: 100%;
    color: #667085;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 750;
  }

  .ob-meta-item svg {
    color: #98a2b3;
    flex: 0 0 auto;
  }

  .ob-meta-item span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ob-operational-line {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #667085;
    font-size: 13px;
    line-height: 1.3;
    font-weight: 750;
  }

  .ob-operational-line span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #98a2b3;
    flex: 0 0 auto;
  }

  .ob-operational-line.active { color: var(--ob-brand); }
  .ob-operational-line.active span { background: #12b76a; }

  .ob-card-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    min-width: max-content;
  }

  .ob-business-card.paused .ob-card-actions,
  .ob-business-card.pending .ob-card-actions {
    grid-column: 2;
    justify-content: flex-start;
    align-self: end;
    margin-top: 8px;
    min-width: 0;
    width: 100%;
  }

  .ob-primary-action,
  .ob-action-light,
  .ob-card-more {
    height: 42px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background 140ms ease;
  }

  .ob-primary-action {
    min-width: 152px;
    border: 0;
    padding: 0 16px;
    color: #ffffff;
    background: linear-gradient(135deg, var(--ob-brand-dark), var(--ob-brand));
    box-shadow: 0 12px 24px rgba(0,79,73,0.18);
  }

  .ob-primary-action.activate {
    min-width: 136px;
    height: 38px;
    font-size: 12px;
    padding: 0 13px;
  }

  .ob-primary-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 32px rgba(0,79,73,0.24);
  }

  .ob-action-light {
    min-width: 96px;
    border: 1px solid rgba(209,213,219,0.95);
    background: #ffffff;
    color: #475467;
    padding: 0 14px;
    box-shadow: 0 8px 18px rgba(16,24,40,0.030);
  }

  .ob-business-card.paused .ob-action-light,
  .ob-business-card.pending .ob-action-light {
    height: 38px;
    min-width: 88px;
    font-size: 12px;
  }

  .ob-action-light:hover {
    color: var(--ob-brand);
    border-color: rgba(0,103,95,0.24);
    background: rgba(0,103,95,0.045);
  }

  .ob-card-more-wrap {
    position: relative;
    z-index: 40;
  }

  .ob-card-more {
    width: 42px;
    min-width: 42px;
    border: 0;
    background: transparent;
    color: #667085;
    padding: 0;
  }

  .ob-business-card.paused .ob-card-more,
  .ob-business-card.pending .ob-card-more {
    width: 38px;
    min-width: 38px;
    height: 38px;
  }

  .ob-card-more:hover {
    background: #f8fafc;
    color: var(--ob-text);
  }

  .ob-more-menu {
    position: absolute;
    right: 0;
    top: 48px;
    width: 220px;
    z-index: 2000;
    border-radius: 18px;
    border: 1px solid rgba(231,222,210,0.96);
    background: #ffffff;
    box-shadow: 0 24px 64px rgba(16,24,40,0.16);
    padding: 7px;
  }

  .ob-more-menu button {
    width: 100%;
    min-height: 42px;
    border: 0;
    border-radius: 13px;
    background: transparent;
    color: #344054;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    text-align: left;
  }

  .ob-more-menu button:hover {
    background: #f8fafc;
    color: var(--ob-text);
  }

  .ob-filter-empty,
  .ob-empty {
    min-height: 360px;
    border-radius: 24px;
    border: 1px dashed rgba(152,162,179,0.55);
    background: rgba(255,255,255,0.82);
    box-shadow: var(--ob-shadow-card);
    display: grid;
    place-items: center;
    text-align: center;
    padding: 34px;
    color: #667085;
  }

  .ob-filter-empty strong,
  .ob-empty h2 {
    margin: 0;
    color: var(--ob-text);
    font-size: 24px;
    line-height: 1.05;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .ob-filter-empty span,
  .ob-empty p {
    max-width: 410px;
    margin: 8px auto 0;
    color: #667085;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 700;
  }

  .ob-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 20px;
    color: white;
    background: linear-gradient(135deg, var(--ob-brand-dark), var(--ob-brand));
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
  }

  .ob-empty-actions {
    margin-top: 18px;
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ob-secondary-btn {
    min-height: 46px;
    border-radius: 15px;
    border: 1px solid rgba(231,222,210,0.96);
    background: #ffffff;
    color: #344054;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
  }

  .ob-card.ob-skeleton-card {
    min-height: 126px;
    border-radius: 18px;
    border: 1px solid rgba(231,222,210,0.96);
    background: rgba(255,255,255,0.86);
    box-shadow: var(--ob-shadow-card);
    padding: 18px;
    display: grid;
    gap: 14px;
    cursor: default;
  }

  .ob-card-head {
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr);
    align-items: center;
    gap: 18px;
  }

  .ob-skeleton-logo,
  .ob-skeleton-lines span,
  .ob-skeleton-actions,
  .ob-skeleton-button {
    background: linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: obShimmer 1.25s infinite linear;
  }

  .ob-skeleton-logo {
    width: 80px;
    height: 80px;
    border-radius: 12px;
  }

  .ob-skeleton-lines {
    display: grid;
    gap: 9px;
    align-content: center;
    width: 100%;
  }

  .ob-skeleton-lines span {
    height: 13px;
    border-radius: 999px;
  }

  .ob-skeleton-lines span:nth-child(1) { width: 52%; }
  .ob-skeleton-lines span:nth-child(2) { width: 72%; }
  .ob-skeleton-lines span:nth-child(3) { width: 44%; }

  .ob-skeleton-button { display: none; }

  .ob-skeleton-actions {
    width: 248px;
    height: 42px;
    border-radius: 12px;
    justify-self: end;
  }

  @keyframes obShimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }

  @media (max-width: 1100px) {
    .ob-business-list.paused,
    .ob-business-list.pending {
      grid-template-columns: 1fr;
    }

    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 72px minmax(0, 1fr) auto;
      align-items: center;
    }

    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 3;
      justify-content: flex-end;
      margin-top: 0;
      width: auto;
    }
  }

  @media (max-width: 980px) {
    .ob-business-card,
    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 72px minmax(0, 1fr);
      align-items: start;
    }

    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 1 / -1;
      width: 100%;
      justify-content: flex-start;
      padding-left: 90px;
      margin-top: 0;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 72px;
      height: 72px;
    }
  }

  @media (max-width: 880px) {
    .ob-page {
      padding: 14px 12px calc(126px + env(safe-area-inset-bottom, 0px));
    }

    .ob-shell { gap: 18px; }

    .ob-header-row {
      grid-template-columns: 1fr;
      align-items: start;
      gap: 16px;
    }

    .ob-create-btn {
      width: 100%;
      min-height: 48px;
    }

    .ob-stats-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .ob-control-bar {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .ob-filter-group { width: 100%; }
    .ob-business-card:hover { transform: none; }
  }

  @media (max-width: 560px) {
    .ob-header-row h1 { font-size: 40px; }

    .ob-stat-card {
      min-height: 74px;
      border-radius: 16px;
      padding: 15px;
    }

    .ob-stat-card strong { font-size: 26px; }
    .ob-control-bar { padding: 0; }

    .ob-business-card,
    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 62px minmax(0, 1fr);
      gap: 13px;
      border-radius: 18px;
      padding: 14px;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 62px;
      height: 62px;
      border-radius: 11px;
      padding: 5px;
    }

    .ob-title-line {
      align-items: flex-start;
      flex-direction: column;
      gap: 6px;
      margin-bottom: 8px;
    }

    .ob-title-line h3,
    .ob-business-card.active .ob-title-line h3 { font-size: 18px; }

    .ob-meta-row { gap: 8px; }

    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      padding-left: 75px;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto auto;
      gap: 8px;
    }

    .ob-primary-action,
    .ob-primary-action.activate {
      min-width: 0;
      width: 100%;
      height: 42px;
      padding: 0 12px;
      font-size: 12.5px;
    }

    .ob-action-light {
      min-width: 42px;
      width: 42px;
      padding: 0;
    }

    .ob-action-light span { display: none; }
    .ob-card-more { width: 42px; min-width: 42px; }

    .ob-more-menu {
      right: -2px;
      width: min(226px, calc(100vw - 42px));
    }
  }

  @media (max-width: 390px) {
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      padding-left: 0;
    }
  }


  /* LOOP OWNER BUSINESSES — PREMIUM COMPACT BUBBLES FIX */
  @media (min-width: 881px) {
    .ob-page {
      padding-top: 14px !important;
      padding-bottom: 64px !important;
    }

    .ob-shell {
      max-width: 1120px !important;
      gap: 13px !important;
    }

    .ob-pretitle-row {
      margin-bottom: 8px !important;
    }

    .ob-header-row {
      gap: 16px !important;
    }

    .ob-header-row h1 {
      font-size: clamp(36px, 3.35vw, 50px) !important;
      letter-spacing: -0.07em !important;
    }

    .ob-header-row p {
      margin-top: 8px !important;
      font-size: 13.5px !important;
      max-width: 560px !important;
    }

    .ob-create-btn,
    .ob-primary-mini-btn {
      min-height: 40px !important;
      border-radius: 13px !important;
      padding: 0 16px !important;
      font-size: 13px !important;
      box-shadow: 0 10px 24px rgba(0,79,73,0.14) !important;
    }

    .ob-stats-row {
      gap: 10px !important;
    }

    .ob-stat-card {
      min-height: 66px !important;
      border-radius: 15px !important;
      padding: 13px 15px !important;
      box-shadow: 0 8px 22px rgba(16,24,40,0.035) !important;
      background: rgba(255,255,255,0.88) !important;
    }

    .ob-stat-accent {
      width: 54px !important;
      height: 2px !important;
    }

    .ob-stat-card strong {
      font-size: 23px !important;
    }

    .ob-stat-card small {
      margin-top: 5px !important;
      font-size: 11px !important;
    }

    .ob-control-bar {
      gap: 12px !important;
      padding-top: 0 !important;
    }

    .ob-search {
      height: 42px !important;
      border-radius: 15px !important;
      padding: 0 14px !important;
      box-shadow: 0 7px 18px rgba(16,24,40,0.03) !important;
    }

    .ob-filter-icon {
      width: 32px !important;
      height: 32px !important;
    }

    .ob-filter-group {
      gap: 7px !important;
    }

    .ob-filter-group button {
      height: 34px !important;
      padding: 0 13px !important;
      font-size: 12px !important;
      box-shadow: none !important;
    }

    .ob-sections-stack {
      gap: 16px !important;
      margin-top: 0 !important;
    }

    .ob-business-section {
      gap: 10px !important;
    }

    .ob-section-header h2 {
      font-size: 16px !important;
    }

    .ob-section-header span {
      min-width: 24px !important;
      height: 22px !important;
      font-size: 11px !important;
    }

    .ob-business-list {
      gap: 10px !important;
    }

    .ob-business-list.paused,
    .ob-business-list.pending {
      gap: 12px !important;
    }

    .ob-business-card,
    .ob-business-card.active {
      min-height: 96px !important;
      border-radius: 16px !important;
      padding: 14px 15px !important;
      grid-template-columns: 64px minmax(0, 1fr) auto !important;
      gap: 14px !important;
      box-shadow: 0 10px 28px rgba(16,24,40,0.045) !important;
      background: rgba(255,255,255,0.92) !important;
    }

    .ob-business-card.paused,
    .ob-business-card.pending {
      min-height: 106px !important;
      border-radius: 16px !important;
      padding: 14px !important;
      grid-template-columns: 60px minmax(0, 1fr) !important;
      gap: 13px !important;
      box-shadow: 0 9px 24px rgba(16,24,40,0.04) !important;
    }

    .ob-business-card:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 16px 38px rgba(16,24,40,0.065) !important;
    }

    .ob-avatar,
    .ob-logo-frame {
      width: 64px !important;
      height: 64px !important;
      border-radius: 11px !important;
      padding: 5px !important;
      box-shadow: 0 7px 16px rgba(16,24,40,0.045) !important;
    }

    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 60px !important;
      height: 60px !important;
    }

    .ob-title-line {
      margin-bottom: 6px !important;
      gap: 8px !important;
    }

    .ob-title-line h3,
    .ob-business-card.active .ob-title-line h3 {
      font-size: 17px !important;
      letter-spacing: -0.035em !important;
    }

    .ob-status {
      border-radius: 7px !important;
      padding: 4px 7px !important;
      font-size: 10px !important;
    }

    .ob-meta-row {
      gap: 9px !important;
    }

    .ob-meta-item {
      font-size: 12px !important;
      gap: 5px !important;
    }

    .ob-meta-item svg {
      width: 12px !important;
      height: 12px !important;
    }

    .ob-operational-line {
      margin-top: 7px !important;
      font-size: 12px !important;
      gap: 7px !important;
    }

    .ob-operational-line span {
      width: 6px !important;
      height: 6px !important;
    }

    .ob-card-actions {
      gap: 7px !important;
    }

    .ob-primary-action,
    .ob-action-light,
    .ob-card-more {
      height: 36px !important;
      border-radius: 10px !important;
      font-size: 12px !important;
      gap: 7px !important;
    }

    .ob-primary-action {
      min-width: 130px !important;
      padding: 0 13px !important;
      box-shadow: 0 9px 18px rgba(0,79,73,0.14) !important;
    }

    .ob-action-light {
      min-width: 82px !important;
      padding: 0 12px !important;
      box-shadow: 0 5px 14px rgba(16,24,40,0.025) !important;
    }

    .ob-card-more {
      width: 36px !important;
      min-width: 36px !important;
    }

    .ob-more-menu {
      top: 42px !important;
      width: 208px !important;
      border-radius: 15px !important;
      box-shadow: 0 18px 48px rgba(16,24,40,0.14) !important;
    }

    .ob-more-menu button {
      min-height: 38px !important;
      border-radius: 11px !important;
      font-size: 12.5px !important;
    }
  }

  @media (max-width: 880px) {
    .ob-page {
      padding-top: 10px !important;
      padding-left: 10px !important;
      padding-right: 10px !important;
      padding-bottom: calc(104px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .ob-shell {
      gap: 12px !important;
    }

    .ob-pretitle-row {
      margin-bottom: 7px !important;
    }

    .ob-header-row {
      gap: 10px !important;
    }

    .ob-header-row h1 {
      font-size: clamp(33px, 10vw, 40px) !important;
      letter-spacing: -0.07em !important;
    }

    .ob-header-row p {
      margin-top: 7px !important;
      font-size: 13px !important;
    }

    .ob-create-btn {
      min-height: 42px !important;
      border-radius: 13px !important;
      font-size: 13px !important;
    }

    .ob-stats-row {
      gap: 8px !important;
    }

    .ob-stat-card {
      min-height: 62px !important;
      border-radius: 14px !important;
      padding: 12px 13px !important;
      box-shadow: 0 7px 18px rgba(16,24,40,0.035) !important;
    }

    .ob-stat-card strong {
      font-size: 22px !important;
    }

    .ob-stat-card small {
      margin-top: 4px !important;
      font-size: 10.5px !important;
    }

    .ob-search {
      height: 40px !important;
      border-radius: 14px !important;
      padding: 0 13px !important;
    }

    .ob-filter-group {
      gap: 7px !important;
    }

    .ob-filter-icon {
      width: 31px !important;
      height: 31px !important;
    }

    .ob-filter-group button {
      height: 32px !important;
      padding: 0 12px !important;
      font-size: 11.5px !important;
      box-shadow: none !important;
    }

    .ob-sections-stack {
      gap: 14px !important;
    }

    .ob-business-section {
      gap: 9px !important;
    }

    .ob-section-header h2 {
      font-size: 15px !important;
    }

    .ob-business-list {
      gap: 9px !important;
    }

    .ob-business-card,
    .ob-business-card.paused,
    .ob-business-card.pending {
      min-height: 0 !important;
      grid-template-columns: 54px minmax(0, 1fr) !important;
      gap: 11px !important;
      border-radius: 15px !important;
      padding: 12px !important;
      box-shadow: 0 8px 22px rgba(16,24,40,0.04) !important;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 54px !important;
      height: 54px !important;
      border-radius: 10px !important;
      padding: 4px !important;
      box-shadow: 0 6px 14px rgba(16,24,40,0.04) !important;
    }

    .ob-title-line {
      gap: 5px !important;
      margin-bottom: 6px !important;
    }

    .ob-title-line h3,
    .ob-business-card.active .ob-title-line h3 {
      font-size: 16px !important;
    }

    .ob-status {
      padding: 3px 7px !important;
      font-size: 9.5px !important;
    }

    .ob-meta-row {
      gap: 7px !important;
    }

    .ob-meta-item {
      font-size: 11.5px !important;
      gap: 5px !important;
    }

    .ob-operational-line {
      margin-top: 6px !important;
      font-size: 11.5px !important;
    }

    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      padding-left: 65px !important;
      gap: 7px !important;
    }

    .ob-primary-action,
    .ob-primary-action.activate {
      height: 38px !important;
      border-radius: 11px !important;
      font-size: 12px !important;
    }

    .ob-action-light,
    .ob-card-more {
      width: 38px !important;
      min-width: 38px !important;
      height: 38px !important;
      border-radius: 11px !important;
    }
  }

  @media (max-width: 390px) {
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      padding-left: 0 !important;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — SIDEPANEL COMMAND CENTER REVAMP
     Web: left professional control panel with create, search, filters and summary.
     Mobile: compact top strip + tighter business feed.
     ========================================================= */

  .ob-kicker {
    display: none !important;
  }

  @media (min-width: 901px) {
    .ob-page {
      padding-top: 26px !important;
      padding-left: clamp(18px, 3vw, 42px) !important;
      padding-right: clamp(18px, 3vw, 42px) !important;
      padding-bottom: 58px !important;
    }

    .ob-shell {
      max-width: 1360px !important;
      gap: 22px !important;
    }

    .ob-header-row {
      grid-template-columns: 1fr !important;
      align-items: start !important;
      padding-left: 292px !important;
      min-height: 90px !important;
    }

    .ob-pretitle-row {
      margin-bottom: 8px !important;
    }

    .ob-header-row h1 {
      font-size: clamp(40px, 3.6vw, 58px) !important;
      letter-spacing: -0.078em !important;
    }

    .ob-header-row p {
      max-width: 660px !important;
      margin-top: 10px !important;
      font-size: 14px !important;
      line-height: 1.42 !important;
    }

    .ob-mobile-command-strip {
      display: none !important;
    }

    .ob-dashboard-layout {
      display: grid !important;
      grid-template-columns: 250px minmax(0, 1fr) !important;
      gap: 42px !important;
      align-items: start !important;
      width: 100% !important;
    }

    .ob-side-panel {
      position: sticky !important;
      top: 86px !important;
      display: grid !important;
      gap: 18px !important;
      align-self: start !important;
      width: 250px !important;
      min-width: 250px !important;
      max-height: calc(100dvh - 108px) !important;
      overflow: auto !important;
      padding: 0 !important;
      scrollbar-width: thin !important;
    }

    .ob-side-create-btn {
      width: 100% !important;
      height: 46px !important;
      min-height: 46px !important;
      border-radius: 14px !important;
      font-size: 13px !important;
      box-shadow: 0 16px 30px rgba(0,79,73,0.16) !important;
    }

    .ob-side-block,
    .ob-side-summary {
      border: 1px solid rgba(231,222,210,0.94) !important;
      background: rgba(255,255,255,0.74) !important;
      border-radius: 20px !important;
      box-shadow: 0 14px 34px rgba(16,24,40,0.045) !important;
      backdrop-filter: blur(16px) !important;
      -webkit-backdrop-filter: blur(16px) !important;
    }

    .ob-side-block {
      padding: 14px !important;
      display: grid !important;
      gap: 12px !important;
    }

    .ob-side-summary {
      padding: 18px !important;
      display: grid !important;
      gap: 14px !important;
    }

    .ob-side-summary-head {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 10px !important;
      padding-bottom: 12px !important;
      border-bottom: 1px solid rgba(231,222,210,0.86) !important;
    }

    .ob-side-summary-head strong {
      color: #101828 !important;
      font-size: 15px !important;
      font-weight: 950 !important;
      letter-spacing: -0.035em !important;
    }

    .ob-side-summary-head span {
      height: 28px !important;
      border-radius: 999px !important;
      border: 1px solid rgba(231,222,210,.9) !important;
      background: #fff !important;
      color: #667085 !important;
      display: inline-flex !important;
      align-items: center !important;
      padding: 0 10px !important;
      font-size: 11px !important;
      font-weight: 850 !important;
    }

    .ob-side-panel .ob-search {
      width: 100% !important;
      height: 42px !important;
      border-radius: 13px !important;
      box-shadow: none !important;
      background: #fff !important;
      padding: 0 12px !important;
    }

    .ob-side-panel .ob-filter-group {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
      width: 100% !important;
      overflow: visible !important;
    }

    .ob-side-panel .ob-filter-icon {
      display: none !important;
    }

    .ob-side-panel .ob-filter-group button {
      height: 34px !important;
      border-radius: 999px !important;
      font-size: 11.5px !important;
      padding: 0 10px !important;
      box-shadow: none !important;
      background: rgba(255,255,255,0.86) !important;
    }

    .ob-side-panel .ob-filter-group button.is-active {
      background: #101828 !important;
      color: #fff !important;
      border-color: #101828 !important;
    }

    .ob-side-panel .ob-stats-row.is-compact {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 0 !important;
    }

    .ob-side-panel .ob-stat-card {
      min-height: 44px !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 8px 0 !important;
      display: grid !important;
      grid-template-columns: 34px minmax(0,1fr) !important;
      grid-template-areas: "value label" !important;
      align-items: center !important;
      column-gap: 12px !important;
      border-bottom: 1px solid rgba(231,222,210,.74) !important;
      overflow: visible !important;
    }

    .ob-side-panel .ob-stat-card:last-child {
      border-bottom: 0 !important;
    }

    .ob-side-panel .ob-stat-accent {
      position: static !important;
      width: 7px !important;
      height: 7px !important;
      border-radius: 999px !important;
      grid-area: label !important;
      margin-left: 0 !important;
      align-self: center !important;
      justify-self: start !important;
      transform: translateX(0) !important;
      display: inline-block !important;
    }

    .ob-side-panel .ob-stat-card strong {
      grid-area: value !important;
      font-size: 20px !important;
      line-height: 1 !important;
      letter-spacing: -0.05em !important;
    }

    .ob-side-panel .ob-stat-card small {
      grid-area: label !important;
      margin: 0 0 0 16px !important;
      font-size: 12px !important;
      line-height: 1 !important;
    }

    .ob-business-feed {
      min-width: 0 !important;
      width: 100% !important;
      display: grid !important;
      gap: 18px !important;
    }

    .ob-business-list.paused,
    .ob-business-list.pending {
      grid-template-columns: 1fr !important;
    }

    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      min-height: 104px !important;
      grid-template-columns: 68px minmax(0,1fr) auto !important;
      align-items: center !important;
      padding: 16px 18px !important;
      border-radius: 18px !important;
      gap: 18px !important;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 68px !important;
      height: 68px !important;
      border-radius: 13px !important;
      padding: 6px !important;
    }

    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 3 !important;
      justify-content: flex-end !important;
      align-self: center !important;
      width: auto !important;
      margin-top: 0 !important;
    }

    .ob-card-actions {
      min-width: max-content !important;
    }

    .ob-primary-action {
      min-width: 152px !important;
      height: 40px !important;
      border-radius: 12px !important;
    }

    .ob-action-light {
      min-width: 92px !important;
      height: 40px !important;
      border-radius: 12px !important;
    }

    .ob-card-more {
      height: 40px !important;
      width: 40px !important;
      min-width: 40px !important;
    }
  }

  @media (min-width: 901px) and (max-width: 1180px) {
    .ob-header-row {
      padding-left: 0 !important;
    }

    .ob-dashboard-layout {
      grid-template-columns: 230px minmax(0, 1fr) !important;
      gap: 26px !important;
    }

    .ob-side-panel {
      width: 230px !important;
      min-width: 230px !important;
    }

    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 62px minmax(0,1fr) !important;
    }

    .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 1 / -1 !important;
      justify-content: flex-start !important;
      padding-left: 80px !important;
      margin-top: -2px !important;
    }
  }

  @media (max-width: 900px) {
    .ob-page {
      padding: 10px 10px calc(106px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .ob-shell {
      gap: 10px !important;
      max-width: 100% !important;
    }

    .ob-header-row {
      display: block !important;
      padding: 0 !important;
    }

    .ob-pretitle-row {
      margin-bottom: 4px !important;
    }

    .ob-back {
      min-height: 30px !important;
      padding: 0 10px !important;
      font-size: 12px !important;
      box-shadow: none !important;
    }

    .ob-header-row h1 {
      font-size: clamp(30px, 9.2vw, 38px) !important;
      line-height: .96 !important;
      letter-spacing: -0.075em !important;
    }

    .ob-header-row p {
      margin-top: 6px !important;
      font-size: 12.8px !important;
      line-height: 1.32 !important;
      max-width: 330px !important;
    }

    .ob-dashboard-layout {
      display: block !important;
    }

    .ob-side-panel {
      display: none !important;
    }

    .ob-mobile-command-strip {
      display: grid !important;
      gap: 9px !important;
    }

    .ob-mobile-create-btn {
      width: 100% !important;
      height: 44px !important;
      min-height: 44px !important;
      border-radius: 13px !important;
      font-size: 13px !important;
      box-shadow: 0 10px 22px rgba(0,79,73,.14) !important;
    }

    .ob-mobile-command-strip .ob-stats-row.is-compact {
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0,1fr)) !important;
      gap: 7px !important;
    }

    .ob-mobile-command-strip .ob-stat-card {
      min-height: 58px !important;
      border-radius: 13px !important;
      padding: 9px 8px !important;
      box-shadow: 0 6px 16px rgba(16,24,40,.032) !important;
      background: rgba(255,255,255,.78) !important;
    }

    .ob-mobile-command-strip .ob-stat-accent {
      width: 34px !important;
      height: 2px !important;
    }

    .ob-mobile-command-strip .ob-stat-card strong {
      font-size: 18px !important;
    }

    .ob-mobile-command-strip .ob-stat-card small {
      margin-top: 4px !important;
      font-size: 9.5px !important;
    }

    .ob-mobile-controls {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
      padding: 0 !important;
    }

    .ob-mobile-controls .ob-search {
      height: 40px !important;
      border-radius: 13px !important;
      padding: 0 12px !important;
      box-shadow: 0 6px 16px rgba(16,24,40,.028) !important;
    }

    .ob-mobile-controls .ob-filter-group {
      width: 100% !important;
      display: flex !important;
      gap: 7px !important;
      overflow-x: auto !important;
      padding-bottom: 1px !important;
    }

    .ob-mobile-controls .ob-filter-icon {
      width: 31px !important;
      height: 31px !important;
      flex: 0 0 31px !important;
    }

    .ob-mobile-controls .ob-filter-group button {
      height: 31px !important;
      padding: 0 11px !important;
      font-size: 11px !important;
      box-shadow: none !important;
    }

    .ob-business-feed {
      display: block !important;
      margin-top: 2px !important;
    }

    .ob-sections-stack {
      gap: 11px !important;
      margin-top: 0 !important;
    }

    .ob-business-section {
      gap: 7px !important;
    }

    .ob-section-header h2 {
      font-size: 14.5px !important;
    }

    .ob-section-header span {
      min-width: 22px !important;
      height: 20px !important;
      font-size: 10px !important;
    }

    .ob-business-list,
    .ob-business-list.paused,
    .ob-business-list.pending {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
    }

    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      min-height: 0 !important;
      grid-template-columns: 52px minmax(0,1fr) auto !important;
      align-items: center !important;
      gap: 10px !important;
      padding: 10px 11px !important;
      border-radius: 15px !important;
      box-shadow: 0 7px 20px rgba(16,24,40,.038) !important;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 52px !important;
      height: 52px !important;
      border-radius: 10px !important;
      padding: 4px !important;
    }

    .ob-title-line {
      flex-direction: row !important;
      align-items: center !important;
      gap: 6px !important;
      margin-bottom: 5px !important;
    }

    .ob-title-line h3,
    .ob-business-card.active .ob-title-line h3 {
      font-size: 15.5px !important;
      line-height: 1.05 !important;
    }

    .ob-status {
      font-size: 9px !important;
      padding: 3px 6px !important;
      border-radius: 7px !important;
    }

    .ob-meta-row {
      gap: 6px !important;
    }

    .ob-meta-item {
      font-size: 10.6px !important;
      gap: 4px !important;
    }

    .ob-meta-item svg {
      width: 11px !important;
      height: 11px !important;
    }

    .ob-operational-line {
      margin-top: 5px !important;
      font-size: 10.8px !important;
      gap: 5px !important;
    }

    .ob-card-actions,
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 3 !important;
      width: auto !important;
      min-width: 34px !important;
      padding-left: 0 !important;
      margin-top: 0 !important;
      display: flex !important;
      justify-content: flex-end !important;
      gap: 0 !important;
    }

    .ob-primary-action,
    .ob-action-light {
      display: none !important;
    }

    .ob-card-more {
      width: 34px !important;
      min-width: 34px !important;
      height: 34px !important;
      border-radius: 10px !important;
      background: transparent !important;
    }

    .ob-card-more::after {
      content: "›";
      font-size: 28px;
      line-height: 1;
      color: #667085;
      transform: translateY(-1px);
    }

    .ob-card-more svg {
      display: none !important;
    }

    .ob-more-menu {
      right: 0 !important;
      top: 38px !important;
      width: min(222px, calc(100vw - 28px)) !important;
      border-radius: 15px !important;
      z-index: 4000 !important;
    }
  }

  @media (max-width: 380px) {
    .ob-mobile-command-strip .ob-stats-row.is-compact {
      gap: 6px !important;
    }

    .ob-mobile-command-strip .ob-stat-card {
      padding: 8px 6px !important;
    }

    .ob-mobile-command-strip .ob-stat-card small {
      font-size: 9px !important;
    }
  }


  /* LOOP OWNER BUSINESSES — TOP SPACE TIGHTEN FIX */
  @media (min-width: 901px) {
    .ob-page {
      padding-top: 12px !important;
    }

    .ob-shell {
      gap: 12px !important;
    }

    .ob-header-row {
      min-height: auto !important;
      margin-bottom: 0 !important;
    }

    .ob-pretitle-row {
      margin-bottom: 6px !important;
    }

    .ob-header-row p {
      margin-top: 6px !important;
    }

    .ob-dashboard-layout {
      margin-top: -18px !important;
      align-items: start !important;
    }

    .ob-side-panel {
      margin-top: -54px !important;
      top: 74px !important;
      gap: 14px !important;
    }

    .ob-side-create-btn {
      margin-bottom: 0 !important;
    }

    .ob-side-block {
      padding: 12px !important;
    }

    .ob-side-summary {
      padding: 15px !important;
    }

    .ob-business-feed {
      gap: 14px !important;
    }

    .ob-sections-stack {
      gap: 14px !important;
    }
  }

  @media (max-width: 900px) {
    .ob-page {
      padding-top: 0 !important;
    }

    .ob-shell {
      gap: 8px !important;
    }

    .ob-header-row {
      margin-top: -4px !important;
      padding-top: 0 !important;
    }

    .ob-pretitle-row {
      margin-bottom: 2px !important;
    }

    .ob-header-row p {
      margin-top: 4px !important;
      margin-bottom: 0 !important;
    }

    .ob-mobile-command-strip {
      margin-top: 0 !important;
      gap: 8px !important;
    }

    .ob-business-feed {
      margin-top: 0 !important;
    }

    .ob-sections-stack {
      gap: 9px !important;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — HEADER GAP REAL FIX
     Console confirmed:
     - fixed mobile header height: 58px
     - body padding-top: 58px
     - .ob-page starts at y=140px
     So there is extra outer spacing before this page. Pull only this page
     upward while keeping enough breathing room below the fixed header.
     ========================================================= */

  @media (min-width: 901px) {
    .ob-page {
      margin-top: -54px !important;
      padding-top: 8px !important;
    }

    .ob-shell {
      gap: 10px !important;
    }

    .ob-header-row {
      min-height: 72px !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }

    .ob-dashboard-layout {
      margin-top: -10px !important;
    }

    .ob-side-panel {
      margin-top: -38px !important;
    }
  }

  @media (max-width: 900px) {
    .ob-page {
      margin-top: -76px !important;
      padding-top: 6px !important;
    }

    .ob-shell {
      gap: 7px !important;
    }

    .ob-header-row {
      margin-top: 0 !important;
      padding-top: 0 !important;
      min-height: auto !important;
    }

    .ob-pretitle-row {
      margin-bottom: 2px !important;
    }

    .ob-header-row h1 {
      margin-top: 0 !important;
    }

    .ob-header-row p {
      margin-top: 4px !important;
      margin-bottom: 0 !important;
    }

    .ob-mobile-command-strip {
      margin-top: 0 !important;
      gap: 7px !important;
    }

    .ob-mobile-command-strip .ob-stats-row.is-compact {
      gap: 6px !important;
    }

    .ob-mobile-command-strip .ob-stat-card {
      min-height: 54px !important;
      padding: 8px 7px !important;
    }

    .ob-mobile-controls {
      gap: 7px !important;
    }

    .ob-business-feed {
      margin-top: 0 !important;
    }
  }

  @media (max-width: 430px) {
    .ob-page {
      margin-top: -80px !important;
    }
  }



  /* LOOP OWNER BUSINESSES — DESKTOP SIDEPANEL TITLE ALIGN FIX */
  @media (min-width: 901px) {
    .ob-side-panel {
      margin-top: -94px !important;
      top: 66px !important;
    }

    .ob-dashboard-layout {
      margin-top: -8px !important;
    }
  }

  @media (min-width: 901px) and (max-height: 760px) {
    .ob-side-panel {
      top: 58px !important;
    }
  }



  /* LOOP OWNER BUSINESSES — STABLE DESKTOP SIDEPANEL IN ALL FILTERS
     Keeps the left command panel at the same visual height whether filter is
     Todas, Activas, Pausadas or Pendientes. */
  @media (min-width: 901px) {
    .ob-page {
      --ob-shell-max: 1360px;
      --ob-shell-pad: clamp(18px, 3vw, 42px);
      --ob-panel-w: 250px;
      --ob-panel-top: 78px;
    }

    .ob-side-panel {
      position: fixed !important;
      top: var(--ob-panel-top) !important;
      left: max(var(--ob-shell-pad), calc((100vw - var(--ob-shell-max)) / 2)) !important;
      width: var(--ob-panel-w) !important;
      min-width: var(--ob-panel-w) !important;
      max-width: var(--ob-panel-w) !important;
      margin-top: 0 !important;
      transform: none !important;
      z-index: 30 !important;
      max-height: calc(100dvh - var(--ob-panel-top) - 24px) !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }

    .ob-dashboard-layout {
      grid-template-columns: var(--ob-panel-w) minmax(0, 1fr) !important;
      gap: 42px !important;
      margin-top: -8px !important;
    }
  }

  @media (min-width: 901px) and (max-width: 1180px) {
    .ob-page {
      --ob-panel-w: 230px;
      --ob-panel-top: 76px;
    }

    .ob-side-panel {
      left: 22px !important;
    }

    .ob-dashboard-layout {
      grid-template-columns: var(--ob-panel-w) minmax(0, 1fr) !important;
      gap: 26px !important;
    }
  }



  /* LOOP OWNER BUSINESSES — RESTORE GRID FLOW AFTER FIXED PANEL
     Previous patch made the side panel fixed, which kept its vertical position
     but removed it from the layout flow. This restores the reserved left column
     so business cards stay to the right, while keeping the panel visually high. */
  @media (min-width: 901px) {
    .ob-page {
      --ob-panel-w: 250px;
      --ob-panel-gap: 42px;
    }

    .ob-dashboard-layout {
      display: grid !important;
      grid-template-columns: var(--ob-panel-w) minmax(0, 1fr) !important;
      gap: var(--ob-panel-gap) !important;
      align-items: start !important;
      width: 100% !important;
      margin-top: -8px !important;
      position: relative !important;
    }

    .ob-side-panel {
      grid-column: 1 !important;
      position: sticky !important;
      top: 78px !important;
      left: auto !important;
      right: auto !important;
      bottom: auto !important;
      width: var(--ob-panel-w) !important;
      min-width: var(--ob-panel-w) !important;
      max-width: var(--ob-panel-w) !important;
      margin-top: -94px !important;
      transform: none !important;
      z-index: 20 !important;
      align-self: start !important;
      justify-self: start !important;
      max-height: calc(100dvh - 96px) !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }

    .ob-business-feed {
      grid-column: 2 !important;
      min-width: 0 !important;
      width: 100% !important;
      max-width: none !important;
      position: relative !important;
      z-index: 1 !important;
    }

    .ob-business-card,
    .ob-business-section,
    .ob-business-list,
    .ob-sections-stack {
      max-width: 100% !important;
    }
  }

  @media (min-width: 901px) and (max-width: 1180px) {
    .ob-page {
      --ob-panel-w: 230px;
      --ob-panel-gap: 26px;
    }

    .ob-side-panel {
      top: 76px !important;
      margin-top: -88px !important;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — REMOVE MORE MENU + MOBILE EDIT BUTTON
     - Removes the three-dot/dropdown action from web and mobile.
     - Keeps "Editar" as the secondary action everywhere.
     - On mobile, "Editar" becomes a clear button that routes to BusinessEditPage.
     ========================================================= */
  .ob-card-more-wrap,
  .ob-card-more,
  .ob-more-menu {
    display: none !important;
  }

  .ob-card-actions {
    grid-template-columns: auto auto !important;
  }

  @media (min-width: 901px) {
    .ob-card-actions {
      gap: 10px !important;
    }

    .ob-action-light {
      min-width: 96px !important;
    }

    .ob-action-light span {
      display: inline !important;
    }
  }

  @media (max-width: 880px) {
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-column: 1 / -1 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(104px, auto) !important;
      width: 100% !important;
      gap: 8px !important;
      padding-left: 65px !important;
      align-items: center !important;
    }

    .ob-action-light {
      width: 100% !important;
      min-width: 104px !important;
      height: 38px !important;
      padding: 0 12px !important;
      border-radius: 11px !important;
      gap: 7px !important;
    }

    .ob-action-light span {
      display: inline !important;
      font-size: 12px !important;
      font-weight: 900 !important;
    }
  }

  @media (max-width: 560px) {
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-template-columns: minmax(0, 1fr) minmax(94px, 108px) !important;
      padding-left: 65px !important;
    }

    .ob-action-light {
      min-width: 94px !important;
      height: 38px !important;
    }
  }

  @media (max-width: 390px) {
    .ob-business-card .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      padding-left: 0 !important;
      grid-template-columns: minmax(0, 1fr) minmax(94px, 108px) !important;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — MOBILE EDIT BUTTON VISIBLE FINAL
     The older mobile rules were compressing .ob-action-light into a tiny icon
     or pushing the actions out of view. This final block makes "Editar" visible
     on every card in mobile and preserves the removed three-dot menu.
     ========================================================= */

  .ob-card-more-wrap,
  .ob-card-more,
  .ob-more-menu {
    display: none !important;
  }

  @media (max-width: 900px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      display: grid !important;
      grid-template-columns: 54px minmax(0, 1fr) !important;
      grid-template-areas:
        "avatar info"
        "actions actions" !important;
      align-items: start !important;
      gap: 10px 11px !important;
      padding: 12px !important;
      overflow: visible !important;
    }

    .ob-business-card > .ob-avatar,
    .ob-business-card > .ob-logo-frame {
      grid-area: avatar !important;
    }

    .ob-business-info {
      grid-area: info !important;
      min-width: 0 !important;
    }

    .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions,
    .ob-business-card.active .ob-card-actions {
      grid-area: actions !important;
      grid-column: 1 / -1 !important;
      width: 100% !important;
      min-width: 0 !important;
      margin: 4px 0 0 !important;
      padding: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(104px, 116px) !important;
      gap: 8px !important;
      align-items: center !important;
      justify-content: stretch !important;
    }

    .ob-primary-action,
    .ob-primary-action.activate,
    .ob-primary-action.calendar {
      width: 100% !important;
      min-width: 0 !important;
      height: 40px !important;
      min-height: 40px !important;
      border-radius: 12px !important;
      padding: 0 12px !important;
      font-size: 12px !important;
      line-height: 1 !important;
      white-space: nowrap !important;
    }

    .ob-action-light {
      width: 100% !important;
      min-width: 104px !important;
      height: 40px !important;
      min-height: 40px !important;
      border-radius: 12px !important;
      padding: 0 12px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 7px !important;
      background: #ffffff !important;
      border: 1px solid rgba(203,213,225,.95) !important;
      color: #344054 !important;
      box-shadow: 0 7px 16px rgba(16,24,40,.035) !important;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    .ob-action-light svg {
      width: 15px !important;
      height: 15px !important;
      flex: 0 0 auto !important;
    }

    .ob-action-light span {
      display: inline !important;
      visibility: visible !important;
      opacity: 1 !important;
      font-size: 12px !important;
      font-weight: 900 !important;
      line-height: 1 !important;
      white-space: nowrap !important;
    }
  }

  @media (max-width: 390px) {
    .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions,
    .ob-business-card.active .ob-card-actions {
      grid-template-columns: 1fr !important;
    }

    .ob-action-light {
      min-width: 0 !important;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — RESPONSIVE EDIT ACTION FIT FINAL
     Fixes tablet widths where "Editar" became a huge full-width row.
     - Desktop/tablet: actions sit to the right.
     - Narrow mobile: actions go below, but compact and proportional.
     ========================================================= */

  @media (min-width: 641px) and (max-width: 1180px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 58px minmax(0, 1fr) auto !important;
      grid-template-areas: "avatar info actions" !important;
      align-items: center !important;
      gap: 14px !important;
      padding: 14px 16px !important;
      min-height: 94px !important;
    }

    .ob-business-card > .ob-avatar,
    .ob-business-card > .ob-logo-frame {
      grid-area: avatar !important;
    }

    .ob-business-info {
      grid-area: info !important;
      min-width: 0 !important;
    }

    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-area: actions !important;
      grid-column: auto !important;
      align-self: center !important;
      justify-self: end !important;
      width: auto !important;
      min-width: 0 !important;
      padding-left: 0 !important;
      margin: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      gap: 8px !important;
    }

    .ob-primary-action,
    .ob-primary-action.activate,
    .ob-primary-action.calendar {
      width: auto !important;
      min-width: 136px !important;
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 12px !important;
      padding: 0 13px !important;
      font-size: 12px !important;
      white-space: nowrap !important;
    }

    .ob-action-light {
      width: auto !important;
      min-width: 88px !important;
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 12px !important;
      padding: 0 13px !important;
      display: inline-flex !important;
      gap: 7px !important;
      white-space: nowrap !important;
    }

    .ob-action-light span {
      display: inline !important;
      font-size: 12px !important;
      font-weight: 900 !important;
    }

    .ob-avatar,
    .ob-logo-frame,
    .ob-business-card.paused .ob-avatar,
    .ob-business-card.pending .ob-avatar {
      width: 58px !important;
      height: 58px !important;
      border-radius: 12px !important;
      padding: 5px !important;
    }
  }

  @media (min-width: 641px) and (max-width: 860px) {
    .ob-primary-action,
    .ob-primary-action.activate,
    .ob-primary-action.calendar {
      min-width: 118px !important;
      padding: 0 11px !important;
    }

    .ob-action-light {
      min-width: 80px !important;
      padding: 0 11px !important;
    }

    .ob-primary-action span {
      font-size: 11.5px !important;
    }
  }

  @media (max-width: 640px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      display: grid !important;
      grid-template-columns: 54px minmax(0, 1fr) !important;
      grid-template-areas:
        "avatar info"
        "actions actions" !important;
      align-items: start !important;
      gap: 10px 11px !important;
      padding: 12px !important;
    }

    .ob-business-card > .ob-avatar,
    .ob-business-card > .ob-logo-frame {
      grid-area: avatar !important;
    }

    .ob-business-info {
      grid-area: info !important;
      min-width: 0 !important;
    }

    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-area: actions !important;
      grid-column: 1 / -1 !important;
      width: 100% !important;
      margin: 4px 0 0 !important;
      padding-left: 0 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(92px, 108px) !important;
      gap: 8px !important;
      align-items: center !important;
    }

    .ob-primary-action,
    .ob-primary-action.activate,
    .ob-primary-action.calendar {
      width: 100% !important;
      min-width: 0 !important;
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 12px !important;
      padding: 0 11px !important;
      font-size: 12px !important;
    }

    .ob-action-light {
      width: 100% !important;
      min-width: 0 !important;
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 12px !important;
      padding: 0 10px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 7px !important;
    }

    .ob-action-light span {
      display: inline !important;
      font-size: 12px !important;
      font-weight: 900 !important;
    }
  }

  @media (max-width: 390px) {
    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      grid-template-columns: 1fr !important;
    }

    .ob-action-light,
    .ob-primary-action,
    .ob-primary-action.activate,
    .ob-primary-action.calendar {
      width: 100% !important;
    }
  }


  /* =========================================================
     LOOP OWNER BUSINESSES — MOBILE/TABLET EDIT TOP RIGHT V39
     Goal:
     - On mobile/tablet, Editar lives at the top-right of the business card.
     - It no longer consumes a full actions row under the business info.
     - The primary calendar action is hidden in this compact card layout.
     ========================================================= */

  @media (max-width: 1180px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      position: relative !important;
      grid-template-columns: 58px minmax(0, 1fr) !important;
      grid-template-areas: "avatar info" !important;
      align-items: start !important;
      min-height: 88px !important;
      padding: 14px 118px 14px 14px !important;
      gap: 12px !important;
      overflow: hidden !important;
    }

    .ob-business-card > .ob-avatar,
    .ob-business-card > .ob-logo-frame {
      grid-area: avatar !important;
    }

    .ob-business-info {
      grid-area: info !important;
      min-width: 0 !important;
      padding-right: 0 !important;
    }

    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      position: absolute !important;
      top: 14px !important;
      right: 14px !important;
      width: auto !important;
      min-width: 0 !important;
      max-width: 98px !important;
      height: 34px !important;
      margin: 0 !important;
      padding: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      gap: 0 !important;
      grid-area: auto !important;
      grid-column: auto !important;
      z-index: 5 !important;
    }

    .ob-card-actions .ob-primary-action,
    .ob-card-actions .ob-primary-action.activate,
    .ob-card-actions .ob-primary-action.calendar {
      display: none !important;
    }

    .ob-action-light.ob-edit-action {
      width: auto !important;
      min-width: 86px !important;
      max-width: 98px !important;
      height: 34px !important;
      min-height: 34px !important;
      max-height: 34px !important;
      padding: 0 11px !important;
      border-radius: 11px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 6px !important;
      background: rgba(255,255,255,0.96) !important;
      border: 1px solid rgba(209,213,219,0.92) !important;
      color: #344054 !important;
      box-shadow: 0 8px 18px rgba(16,24,40,0.04) !important;
      font-size: 12px !important;
      font-weight: 900 !important;
      line-height: 1 !important;
      white-space: nowrap !important;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    .ob-action-light.ob-edit-action svg {
      width: 14px !important;
      height: 14px !important;
      flex: 0 0 14px !important;
    }

    .ob-action-light.ob-edit-action span {
      display: inline !important;
      visibility: visible !important;
      opacity: 1 !important;
      font-size: 12px !important;
      font-weight: 900 !important;
      line-height: 1 !important;
      white-space: nowrap !important;
    }

    .ob-title-line {
      padding-right: 0 !important;
      max-width: 100% !important;
    }

    .ob-meta-row,
    .ob-operational-line {
      max-width: 100% !important;
    }
  }

  @media (max-width: 640px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      grid-template-columns: 54px minmax(0, 1fr) !important;
      padding: 12px 108px 12px 12px !important;
      gap: 11px !important;
      min-height: 82px !important;
    }

    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      top: 12px !important;
      right: 12px !important;
      max-width: 90px !important;
      height: 32px !important;
    }

    .ob-action-light.ob-edit-action {
      min-width: 78px !important;
      max-width: 90px !important;
      height: 32px !important;
      min-height: 32px !important;
      max-height: 32px !important;
      padding: 0 9px !important;
      border-radius: 10px !important;
      font-size: 11.5px !important;
    }

    .ob-action-light.ob-edit-action svg {
      width: 13px !important;
      height: 13px !important;
      flex-basis: 13px !important;
    }

    .ob-action-light.ob-edit-action span {
      font-size: 11.5px !important;
    }

    .ob-title-line {
      gap: 5px !important;
      margin-bottom: 5px !important;
    }

    .ob-title-line h3,
    .ob-business-card.active .ob-title-line h3 {
      font-size: 15.5px !important;
      max-width: 100% !important;
    }

    .ob-status {
      padding: 3px 6px !important;
      font-size: 9.5px !important;
    }
  }

  @media (max-width: 390px) {
    .ob-business-card,
    .ob-business-card.active,
    .ob-business-card.paused,
    .ob-business-card.pending {
      padding-right: 96px !important;
    }

    .ob-card-actions,
    .ob-business-card.active .ob-card-actions,
    .ob-business-card.paused .ob-card-actions,
    .ob-business-card.pending .ob-card-actions {
      right: 10px !important;
      max-width: 82px !important;
    }

    .ob-action-light.ob-edit-action {
      min-width: 74px !important;
      max-width: 82px !important;
      padding: 0 8px !important;
    }
  }


  /* =========================================================
     LOOP OWNER BUSINESSES — MOBILE BACKGROUND FILL V41
     V40 reduced the extra bottom scroll, but in Chrome mobile short lists could
     leave the body background visible as a white strip after the beige page.
     Keep the scroll lock, but make the page/body fill the visible viewport with
     the same OwnerBusinesses background.
     ========================================================= */
  @media (max-width: 900px) {
    html:has(.ob-page),
    body:has(.ob-page) {
      min-height: 100dvh !important;
      background:
        radial-gradient(circle at 18% -10%, rgba(0,103,95,0.045), transparent 360px),
        linear-gradient(180deg, #fffaf3 0%, #f8f1e8 100%) !important;
      overscroll-behavior-y: contain !important;
    }

    .ob-page {
      min-height: 100dvh !important;
      height: auto !important;
      background:
        radial-gradient(circle at 18% -10%, rgba(0,103,95,0.045), transparent 360px),
        linear-gradient(180deg, #fffaf3 0%, #f8f1e8 100%) !important;
      padding-bottom: max(18px, calc(env(safe-area-inset-bottom, 0px) + 14px)) !important;
      margin-bottom: 0 !important;
      overflow-x: hidden !important;
    }

    body.is-mobile-app .ob-page {
      min-height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
      padding-bottom: calc(var(--loop-safe-bottom, max(env(safe-area-inset-bottom, 0px), 34px)) + 14px) !important;
    }

    .ob-shell,
    .ob-dashboard-layout,
    .ob-business-feed,
    .ob-sections-stack {
      min-height: 0 !important;
    }
  }

  @media (max-width: 430px) {
    .ob-page {
      padding-bottom: max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px)) !important;
    }

    body.is-mobile-app .ob-page {
      padding-bottom: calc(var(--loop-safe-bottom, max(env(safe-area-inset-bottom, 0px), 34px)) + 12px) !important;
    }
  }


  /* =========================================================
     LOOP OWNER BUSINESSES — MOBILE END REACH SAFE AREA V42
     V41 removed too much bottom room. On Android/Capacitor the system
     navigation bar can still cover the final business card. Keep the page
     locked to the app viewport, but add a real scroll-end buffer so the last
     card can be pulled fully above the Android navigation buttons.
     ========================================================= */
  @media (max-width: 900px) {
    html:has(.ob-page),
    body:has(.ob-page),
    body:has(.ob-page) #root,
    body:has(.ob-page) .App {
      min-height: 100dvh !important;
      height: auto !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      overscroll-behavior-y: contain !important;
    }

    .ob-page {
      --ob-mobile-bottom-reach: 92px;
      min-height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
      height: auto !important;
      overflow: visible !important;
      padding-bottom: calc(var(--ob-mobile-bottom-reach) + max(env(safe-area-inset-bottom, 0px), 24px)) !important;
      margin-bottom: 0 !important;
    }

    body.is-mobile-app .ob-page {
      --ob-mobile-bottom-reach: 118px;
      padding-bottom: calc(var(--ob-mobile-bottom-reach) + max(env(safe-area-inset-bottom, 0px), var(--loop-safe-bottom, 34px), 34px)) !important;
    }

    .ob-shell,
    .ob-dashboard-layout,
    .ob-business-feed,
    .ob-sections-stack,
    .ob-business-section,
    .ob-business-list {
      overflow: visible !important;
      min-height: 0 !important;
    }

    .ob-business-list:last-child::after,
    .ob-sections-stack::after {
      content: "";
      display: block;
      height: 1px;
      flex: 0 0 auto;
    }
  }

  @media (max-width: 430px) {
    .ob-page {
      --ob-mobile-bottom-reach: 104px;
    }

    body.is-mobile-app .ob-page {
      --ob-mobile-bottom-reach: 136px;
    }
  }



  /* =========================================================
     LOOP OWNER BUSINESSES — MOBILE SCROLL CONTAINER V43
     Debug showed the page had bottom padding, but the last card still stayed
     under the Android navigation area. Make OwnerBusinessesPage itself the
     native scroll container and put the end buffer inside that same scroller.
     ========================================================= */
  @media (max-width: 900px) {
    body.is-mobile-app:has(.ob-page),
    body.is-mobile-app:has(.ob-page) #root,
    body.is-mobile-app:has(.ob-page) .App {
      height: 100dvh !important;
      min-height: 100dvh !important;
      max-height: 100dvh !important;
      overflow: hidden !important;
      overscroll-behavior-y: none !important;
      background:
        radial-gradient(circle at 18% -10%, rgba(0,103,95,0.045), transparent 360px),
        linear-gradient(180deg, #fffaf3 0%, #f8f1e8 100%) !important;
    }

    body.is-mobile-app .ob-page {
      --ob-native-end-reach: 210px;
      height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
      min-height: 0 !important;
      max-height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      -webkit-overflow-scrolling: touch !important;
      overscroll-behavior-y: contain !important;
      touch-action: pan-y !important;
      padding-bottom: 0 !important;
      scroll-padding-bottom: var(--ob-native-end-reach) !important;
    }

    body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--ob-native-end-reach) + max(env(safe-area-inset-bottom, 0px), 22px)) !important;
    }

    body.is-mobile-app .ob-dashboard-layout,
    body.is-mobile-app .ob-business-feed,
    body.is-mobile-app .ob-sections-stack,
    body.is-mobile-app .ob-business-section,
    body.is-mobile-app .ob-business-list {
      overflow: visible !important;
      min-height: 0 !important;
    }

    body.is-mobile-app .ob-business-list:last-child::after,
    body.is-mobile-app .ob-sections-stack::after {
      content: "" !important;
      display: block !important;
      height: 1px !important;
      flex: 0 0 auto !important;
    }
  }

  @media (max-width: 430px) {
    body.is-mobile-app .ob-page {
      --ob-native-end-reach: 240px;
    }
  }


  /* =========================================================
     LOOP DEVICE FOOTER FIT V44
     Scope: OwnerBusinessesPage end reach / bottom fit.
     Goal:
     - Do not lock html/body/#root from this page.
     - Android: last card can stop above system navigation buttons.
     - iOS: only real safe-area inset, no artificial footer.
     ========================================================= */
  @media (max-width: 900px) {
    .ob-page {
      --loop-owner-bottom-safe: env(safe-area-inset-bottom, 0px);
      --ob-native-end-reach: calc(var(--loop-owner-bottom-safe) + 24px);

      min-height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 24px) !important;
      scroll-padding-bottom: calc(var(--loop-owner-bottom-safe) + 24px) !important;
    }

    body.is-mobile-app.is-android-app .ob-page,
    html.is-android-app body.is-mobile-app .ob-page {
      --loop-owner-bottom-safe: max(
        env(safe-area-inset-bottom, 0px),
        var(--safe-area-inset-bottom, 0px),
        var(--loop-safe-bottom, 0px)
      );
      --ob-native-end-reach: calc(var(--loop-owner-bottom-safe) + 24px);
    }

    body.is-mobile-app.is-ios-app .ob-page,
    html.is-ios-app body.is-mobile-app .ob-page {
      --loop-owner-bottom-safe: env(safe-area-inset-bottom, 0px);
      --ob-native-end-reach: calc(var(--loop-owner-bottom-safe) + 18px);
    }

    body.is-mobile-app:has(.ob-page),
    body.is-mobile-app:has(.ob-page) #root,
    body.is-mobile-app:has(.ob-page) .App {
      height: auto !important;
      min-height: 100% !important;
      max-height: none !important;
      overflow-y: auto !important;
      overscroll-behavior-y: auto !important;
    }

    body.is-mobile-app .ob-page {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-y !important;
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 24px) !important;
    }

    body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 24px) !important;
    }

    body.is-mobile-app .ob-business-list:last-child::after,
    body.is-mobile-app .ob-sections-stack::after {
      content: "" !important;
      display: block !important;
      height: 1px !important;
      flex: 0 0 auto !important;
    }
  }

  @media (max-width: 430px) {
    body.is-mobile-app .ob-page {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 22px) !important;
    }

    body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 22px) !important;
    }
  }


  /* =========================================================
     LOOP OWNER BUSINESSES — DEVICE SCROLL END STOP V46
     Fixes the giant blank scroll area after the last business card.
     Contract:
     - Do not make this page a fixed/full-height scroll container.
     - Do not use artificial "end reach" buffers.
     - Android: only reserve the real system nav guard + small breathing room.
     - iOS: only reserve real safe-area inset + small breathing room.
     ========================================================= */
  @media (max-width: 900px) {
    body.is-mobile-app .ob-page,
    html.is-mobile-app body.is-mobile-app .ob-page {
      --loop-owner-bottom-safe: env(safe-area-inset-bottom, 0px) !important;
      --ob-native-end-reach: 0px !important;
      --ob-end-breathing-room: 8px !important;

      min-height: 0 !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      padding-bottom: 0 !important;
      scroll-padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }

    body.is-mobile-app.is-android-app .ob-page,
    html.is-android-app body.is-mobile-app .ob-page {
      --loop-owner-bottom-safe: max(
        env(safe-area-inset-bottom, 0px),
        var(--safe-area-inset-bottom, 0px),
        var(--loop-safe-bottom, 0px)
      ) !important;
      --ob-end-breathing-room: 8px !important;
    }

    body.is-mobile-app.is-ios-app .ob-page,
    html.is-ios-app body.is-mobile-app .ob-page {
      --loop-owner-bottom-safe: env(safe-area-inset-bottom, 0px) !important;
      --ob-end-breathing-room: 8px !important;
    }

    body.is-mobile-app .ob-shell,
    html.is-mobile-app body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + var(--ob-end-breathing-room)) !important;
      margin-bottom: 0 !important;
      min-height: 0 !important;
    }

    body.is-mobile-app .ob-dashboard-layout,
    body.is-mobile-app .ob-business-feed,
    body.is-mobile-app .ob-sections-stack,
    body.is-mobile-app .ob-business-section,
    body.is-mobile-app .ob-business-list {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
    }

    body.is-mobile-app .ob-business-section:last-child,
    body.is-mobile-app .ob-business-list:last-child,
    body.is-mobile-app .ob-business-card:last-child {
      margin-bottom: 0 !important;
    }

    body.is-mobile-app .ob-page::after,
    body.is-mobile-app .ob-shell::after,
    body.is-mobile-app .ob-dashboard-layout::after,
    body.is-mobile-app .ob-business-feed::after,
    body.is-mobile-app .ob-sections-stack::after,
    body.is-mobile-app .ob-business-section::after,
    body.is-mobile-app .ob-business-list::after,
    body.is-mobile-app .ob-business-list:last-child::after {
      content: none !important;
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  @media (max-width: 430px) {
    body.is-mobile-app.is-android-app .ob-shell,
    html.is-android-app body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 8px) !important;
    }

    body.is-mobile-app.is-ios-app .ob-shell,
    html.is-ios-app body.is-mobile-app .ob-shell {
      padding-bottom: calc(var(--loop-owner-bottom-safe) + 8px) !important;
    }
  }


`;
